import { Link } from '@tanstack/react-router';
import { ChevronRight } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@nucleus-commerce/ui/components/button';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@nucleus-commerce/ui/components/collapsible';
import { cn } from '@nucleus-commerce/ui/lib/utils';

import type { DocsOperation } from '../../generated/reference';
import { CodeBlock } from '../code-block';

import { exampleCode, isErrorStatus } from './endpoint-example';
import { EndpointSection } from './endpoint-section';
import { StatusCode } from './status-code';

/** Section and anchor of the error catalog in the Conventions guide. */
const CATALOG = { slug: 'conventions', hash: 'error-codes' } as const;

/** The error statuses the spec declares, with the recorded error envelope where there is one. */
export function EndpointErrors({ operation }: { operation: DocsOperation }) {
  const { t } = useTranslation('docs');
  const statuses = Object.keys(operation.responses).filter(isErrorStatus).sort();
  if (statuses.length === 0) return null;

  return (
    <EndpointSection id="errors" title={t('endpoint.errors.title')}>
      <p className="text-muted-foreground text-sm">
        {t('endpoint.errors.description')}{' '}
        <Link
          to="/docs/guides/$slug"
          params={{ slug: CATALOG.slug }}
          hash={CATALOG.hash}
          className="text-foreground font-medium underline underline-offset-4"
        >
          {t('endpoint.errors.catalog')}
        </Link>
      </p>
      <ul className="divide-y rounded-lg border">
        {statuses.map((status) => {
          const response = operation.responses[status];
          if (!response) return null;
          const example = response.example ?? operation.examples.responses[status];
          return (
            <ErrorRow
              key={status}
              status={status}
              description={response.description}
              example={example}
            />
          );
        })}
      </ul>
    </EndpointSection>
  );
}

function ErrorRow({
  status,
  description,
  example,
}: {
  status: string;
  description: string;
  example: unknown;
}) {
  const { t } = useTranslation('docs');
  const [open, setOpen] = useState(false);
  const code = errorCode(example);

  return (
    <Collapsible asChild open={open} onOpenChange={setOpen}>
      <li data-status={status} className="flex flex-col gap-2 px-4 py-3 text-sm">
        <div className="flex flex-wrap items-center gap-2">
          <StatusCode status={status} />
          <span className="min-w-0 flex-1">{description}</span>
          {code ? (
            <Link
              to="/docs/guides/$slug"
              params={{ slug: CATALOG.slug }}
              hash={CATALOG.hash}
              className="font-mono text-xs underline underline-offset-4"
            >
              {code}
            </Link>
          ) : null}
          {example !== undefined ? (
            <CollapsibleTrigger asChild>
              <Button type="button" variant="ghost" size="sm" className="h-7 text-xs">
                <ChevronRight
                  aria-hidden
                  className={cn('size-3.5 transition-transform', open && 'rotate-90')}
                />
                {open ? t('endpoint.errors.hideExample') : t('endpoint.errors.showExample')}
              </Button>
            </CollapsibleTrigger>
          ) : null}
        </div>
        {example !== undefined ? (
          <CollapsibleContent>
            <CodeBlock {...exampleCode(example)} />
          </CollapsibleContent>
        ) : null}
      </li>
    </Collapsible>
  );
}

/** `error.code` of a recorded error envelope. */
function errorCode(example: unknown): string | null {
  if (typeof example !== 'object' || example === null || !('error' in example)) return null;
  const { error } = example;
  if (typeof error !== 'object' || error === null || !('code' in error)) return null;
  return typeof error.code === 'string' ? error.code : null;
}
